import { Bus } from "baconjs"

import {
    MediaValue,
    ProductResult,
    valueOf
} from "@encoway/c-services-js-client"
import { L10n } from "@encoway/l10n"

import { Events } from "../../constants"
import { Constants } from "./constants"
import { download, getProductInfo } from "./helper"
import {
    IVisualizationCloud,
    IVisualizationExtensionIFCHelper,
    IVisualizationOption,
    IVisualizationServer
} from "./visualizationService.types"

const getIFCTemplateAndState = async (
    cloud: IVisualizationCloud,
    product: ProductResult
): Promise<{ ifcTemplate: string; state: object }> => {
    const ifcCharacteristic = valueOf(
        product,
        Constants.Characteristic.IFC
    ) as MediaValue

    const ifcTemplate = ifcCharacteristic?.uri
        ? await fetch(ifcCharacteristic.uri).then((response) => response.text())
        : ""

    const rootNode = cloud
        .graph()
        .nodes()
        .find((node) => node.incoming().length === 0)

    return {
        ifcTemplate,
        state: rootNode !== undefined ? rootNode.state.state : {}
    }
}

export function generateIFC(
    vis: IVisualizationServer,
    option: IVisualizationOption,
    eventBus: Bus<any>,
    productId: string
) {
    const cloud = vis.cloud
    cloud.addPendingEvent(Constants.PendingEvent.CreateIFC)

    generateAndDownloadIFC(vis, option, productId)
        .catch((err) => {
            console.error("Error while creating IFC file", err)
            eventBus.push({
                type: Events.ShowError,
                message: L10n.format("Application.Visualization.IFC.error")
            })
        })
        .finally(() =>
            cloud.removePendingEvent(Constants.PendingEvent.CreateIFC)
        )
}

async function generateAndDownloadIFC(
    vis: IVisualizationServer,
    option: IVisualizationOption,
    productId: string
) {
    const cloud = vis.cloud

    const product = await getProductInfo(vis, productId)
    if (!product) {
        return
    }

    const ifcHelper = (await vis.useExtension(
        Constants.Extension.IFCHelper,
        vis.version,
        cloud.token
    )) as IVisualizationExtensionIFCHelper

    const { ifcTemplate, state } = await getIFCTemplateAndState(cloud, product)

    const ifcFile = await ifcHelper.exportIFC(cloud, ifcTemplate, state, {
        alternativeModel: option.alternativeModel ?? true,
        draco: option.draco ?? {}
    })

    const request = new Request(`${option.baseUrl}/convert`, {
        method: "POST",
        headers: new Headers({
            Accept: option.zip ? "application/zip" : "application/x-step",
            "Content-Type": "application/vnd.encoway.ifc",
            Authorization: "Bearer " + cloud.token
        }),
        body: JSON.stringify(ifcFile)
    })

    const response = await fetch(request)
    if (!response.ok) {
        throw response
    }

    const blob = await response.blob()
    const filename = option.zip ? "model.ifcZIP" : "model.ifc"
    download(blob, filename)
}
